import { computed, ref } from 'vue'

import { useDataLayer } from '@toni/brand-ui'

import type { ProductCardPlacementType, ProductCardTrackingType } from './types'

/**
 * Calculate the relative position of a product card within content blocks
 * @param index - The index of the product card
 * @param contentBlocks - Array of content block types
 * @returns The relative position as a percentage string
 */
export const getProductCardRelativePosition = (index: number | undefined, contentBlocks: string[] | undefined): string => {
  if (!contentBlocks || index === undefined || index === -1) {
    return ''
  }

  return `${((index + 1) * 100 / contentBlocks.length).toFixed(0)}%`
}

/**
 * Calculate the position of a product card within an array
 * @param index - The index to find
 * @param array - The array of indices
 * @returns The position (1-based)
 */
export const getProductCardPosition = (index: number | undefined, array: number[]): number => {
  if (!array || index === undefined || index === -1) {
    return 0
  }
  return array.indexOf(index) + 1
}

/**
 * Setup product card tracking data for either product card, product card groups or product card comparison table to be provided
 * to components through provide/inject
 *
 * @param placementType - Type of product card placement (product_card, product_card_group, product_card_comparison_table)
 * @param contentBlocks - Optional array of content block types
 * @param currentContentBlockIndex - Optional index of the product card content block
 * @returns Object containing tracking data and refs
 */
export const useProductCardTracking = ({
  placementType,
  contentBlocks,
  currentContentBlockIndex
}: {
  placementType: ProductCardPlacementType,
  contentBlocks?: string[],
  currentContentBlockIndex?: number
}) => {
  // Calculate total number of product card content block types in content blocks
  const totalNumberOfProductCardContentBlockType = computed(() =>
    contentBlocks
      ? contentBlocks.map((paragraph, index) =>
        paragraph === 'productCard' ||
        paragraph === 'productCardGroup' ||
        paragraph === 'productCardComparisonTable'
          ? index
          : -1).filter((index) => index !== -1)
      : []
  )

  // Calculate position information
  const productCardPosition = `${computed(() => getProductCardPosition(currentContentBlockIndex, totalNumberOfProductCardContentBlockType.value)).value}/${totalNumberOfProductCardContentBlockType.value.length}`
  const productCardRelativePosition = computed(() => getProductCardRelativePosition(currentContentBlockIndex, contentBlocks))

  const trackingData: ProductCardTrackingType = {
    product_card_type: placementType,
    product_card_position: productCardPosition,
    product_card_rel_position: productCardRelativePosition.value
  }

  return {
    trackingData
  }
}
/**
 * Setup intersection observer for tracking product card impressions which is when the product card is in view
 *
 * @param placementType - Type of product card placement (product_card, product_card_group, product_card_comparison_table)
 * @param productCardPosition - Position of the product card
 * @param productCardRelativePosition - Relative position of the product card
 * @param bauerProductCardId - Optional ID of the product card
 */
export const useIntersectionObserver = ({
  placementType,
  bauerProductCardId,
  productCardPosition,
  productCardRelativePosition
}: {
  placementType?: ProductCardPlacementType,
  productCardPosition?: string,
  productCardRelativePosition?: string,
  bauerProductCardId?: string
}) => {
  if (
    !placementType ||
    !productCardPosition ||
    !productCardRelativePosition ||
    !bauerProductCardId
  ) {
    return {
      productCardScrollTrackingRef: undefined,
      setupIntersectionObserver: undefined
    }
  }

  // Reference for intersection observer
  const productCardScrollTrackingRef = ref(null)

  const setupIntersectionObserver = () => {
    const dataLayer = useDataLayer()
    let productCardInView = false

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0] && entries[0].isIntersecting && productCardInView === false) {
          productCardInView = true

          const productCardTrackingData = {
            event: 'product_card_impression',
            product_card_id: bauerProductCardId,
            placement: placementType,
            product_card_position: productCardPosition,
            product_card_rel_position: productCardRelativePosition
          }

          dataLayer.push(productCardTrackingData)
        }
      },
      { threshold: 1.0 }
    )

    if (productCardScrollTrackingRef.value) {
      observer.observe(productCardScrollTrackingRef.value)
    }
  }

  return {
    productCardScrollTrackingRef,
    setupIntersectionObserver
  }
}
